import React, { useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Card, Spinner, Button, Dropdown, Badge } from "react-bootstrap";
import { orderService } from "../services/orderService";
import { authService } from "../services/authService";
import Header from "../components/Layout/Header";
import Footer from "../components/Layout/Footer";
import api from '../config/ApiConfig';

const OrderHistoryPage = () => {
  const history = useHistory();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('6months');
  const [availableYears, setAvailableYears] = useState([]);
  const currentUser = useMemo(() => authService.getCurrentUser(), []);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await orderService.getOrderHistory();

        // Extract paginated data if present
        const ordersArray = data.data || data;

        // Sort orders by most recent first
        const sortedOrders = ordersArray.sort((a, b) =>
          new Date(b.DateCommande || b.created_at) - new Date(a.DateCommande || a.created_at)
        );

        setOrders(sortedOrders);

        // Calculate available years from orders
        const years = [...new Set(sortedOrders.map(order => {
          const date = new Date(order.DateCommande || order.created_at);
          return date.getFullYear();
        }))].sort((a, b) => b - a);

        setAvailableYears(years);
      } catch (error) {
        console.error("Erreur lors de la récupération de l'historique:", error);

        // Gérer différents types d'erreurs
        if (error.response?.status === 429) {
          setError("Trop de requêtes. Veuillez patienter quelques instants avant de réessayer.");
        } else if (error.response?.status === 500) {
          setError("Erreur serveur. Veuillez réessayer plus tard.");
        } else if (!error.response) {
          setError("Problème de connexion. Vérifiez votre connexion Internet.");
        } else {
          setError("Une erreur est survenue lors du chargement de l'historique.");
        }
      } finally {
        setLoading(false);
      }
    };

    if (currentUser?.id) {
      fetchOrders();
    }
  }, [currentUser]);

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    // Force refresh pour bypass le cache
    orderService.getOrderHistory(true).then(data => {
      const ordersArray = data.data || data;
      const sortedOrders = ordersArray.sort((a, b) =>
        new Date(b.DateCommande || b.created_at) - new Date(a.DateCommande || a.created_at)
      );
      setOrders(sortedOrders);
      const years = [...new Set(sortedOrders.map(order => {
        const date = new Date(order.DateCommande || order.created_at);
        return date.getFullYear();
      }))].sort((a, b) => b - a);
      setAvailableYears(years);
    }).catch(err => {
      console.error("Erreur lors de la récupération de l'historique:", err);
      if (err.response?.status === 429) {
        setError("Trop de requêtes. Veuillez patienter quelques instants avant de réessayer.");
      } else {
        setError("Une erreur est survenue lors du chargement de l'historique.");
      }
    }).finally(() => {
      setLoading(false);
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateKey = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusLabel = (statutCommande, statutPaiement) => {
    // Map status based on StatuCommande field
    const statusMap = {
      1: { label: 'En cours de livraison', variant: 'primary' },
      2: { label: 'Livré', variant: 'success' },
      3: { label: 'En cours de préparation', variant: 'info' },
      4: { label: 'En cours de validation', variant: 'warning' },
      6: { label: 'Annulé', variant: 'danger' }
    };

    const status = statusMap[statutCommande] || { label: 'En attente', variant: 'secondary' };

    // Add payment status if available
    if (statutPaiement === 1 || statutPaiement === '1') {
      return { label: `${status.label} - Payé`, variant: status.variant };
    } else if (statutPaiement === 0 || statutPaiement === '0') {
      return { label: `${status.label} - Non payé`, variant: 'warning' };
    }

    return status;
  };

  const filterOrders = () => {
    const now = new Date();

    if (selectedFilter === '6months') {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(now.getMonth() - 6);

      return orders.filter(order => {
        const orderDate = new Date(order.DateCommande || order.created_at);
        return orderDate >= sixMonthsAgo;
      });
    } else {
      // Filter by specific year
      const year = parseInt(selectedFilter);
      return orders.filter(order => {
        const orderDate = new Date(order.DateCommande || order.created_at);
        return orderDate.getFullYear() === year;
      });
    }
  };

  const groupOrdersByDay = (filteredOrders) => {
    const grouped = {};

    filteredOrders.forEach(order => {
      const dateKey = formatDateKey(order.DateCommande || order.created_at);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(order);
    });

    return grouped;
  };

  const handleDownloadInvoice = (order) => {
    try {
      const invoiceFileName = order?.NumeroFacture;

      if (!invoiceFileName) {
        console.error("Aucune facture disponible pour cette commande");
        return;
      }

      console.log('📄 Nom fichier facture (liste):', invoiceFileName);

      let baseUrl = api.defaults.baseURL;

      // Retirer /api à la fin (comme sur la page détail)
      if (baseUrl.endsWith('/api')) {
        baseUrl = baseUrl.slice(0, -4);
      } else if (baseUrl.endsWith('/api/')) {
        baseUrl = baseUrl.slice(0, -5);
      }

      // S'assurer qu'il n'y a pas de / en trop à la fin
      baseUrl = baseUrl.replace(/\/+$/, '');

      // URL finale : https://ton-backend/factures/FAC-CF-XXXX-YYYYMMDD.pdf
      const invoiceUrl = `${baseUrl}/factures/${invoiceFileName}`;

      console.log('📄 URL complète facture (liste):', invoiceUrl);
      window.open(invoiceUrl, '_blank');
    } catch (error) {
      console.error("Erreur lors du téléchargement de la facture:", error);
    }
  };


  const filteredOrders = filterOrders();
  const groupedOrders = groupOrdersByDay(filteredOrders);

  return (
    <>
      <Header />
      <div className="container mt-5 mb-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">Historique des livraisons</h2>

          <Dropdown>
            <Dropdown.Toggle variant="outline-primary" id="dropdown-filter">
              <i className="fas fa-filter me-2"></i>
              {selectedFilter === '6months' ? 'Derniers 6 mois' : `Année ${selectedFilter}`}
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item
                active={selectedFilter === '6months'}
                onClick={() => setSelectedFilter('6months')}
              >
                Derniers 6 mois
              </Dropdown.Item>
              <Dropdown.Divider />
              {availableYears.map(year => (
                <Dropdown.Item
                  key={year}
                  active={selectedFilter === year.toString()}
                  onClick={() => setSelectedFilter(year.toString())}
                >
                  Année {year}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {error ? (
          <Card className="text-center p-5 border-warning">
            <Card.Body>
              <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
              <h5 className="text-warning mb-3">Une erreur est survenue</h5>
              <p className="text-muted mb-4">{error}</p>
              <Button variant="warning" onClick={handleRetry}>
                <i className="fas fa-redo me-2"></i>
                Réessayer
              </Button>
            </Card.Body>
          </Card>
        ) : loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Chargement...</span>
            </Spinner>
          </div>
        ) : Object.keys(groupedOrders).length === 0 ? (
          <Card className="text-center p-5">
            <Card.Body>
              <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
              <p className="text-muted">Aucune commande pour la période sélectionnée</p>
            </Card.Body>
          </Card>
        ) : (
          <div>
            {Object.entries(groupedOrders).map(([dateKey, dayOrders]) => (
              <div key={dateKey} className="mb-4">
                <h5 className="text-primary mb-3" style={{ textTransform: 'capitalize' }}>
                  <i className="fas fa-calendar-day me-2"></i>
                  {dateKey}
                </h5>

                <div className="row g-3">
                  {dayOrders.map((order) => {
                    const status = getStatusLabel(order.StatuCommande, order.StatuPaiement);

                    return (
                      <div key={order.id} className="col-12">
                        <Card className="shadow-sm hover-shadow">
                          <Card.Body>
                            <div className="row align-items-center">
                              <div className="col-md-2">
                                <div className="text-center">
                                  <i className="fas fa-truck fa-2x text-primary mb-2"></i>
                                  <div className="small text-muted">
                                    {formatTime(order.DateCommande || order.created_at)}
                                  </div>
                                  <div className="small fw-bold">
                                    #{order.id}
                                  </div>
                                </div>
                              </div>

                              <div className="col-md-4">
                                <h6 className="mb-1">
                                  <i className="fas fa-map-marker-alt me-2 text-muted"></i>
                                  Livraison
                                </h6>
                                <p className="mb-1 small">{order.AdresseLivraison}</p>
                                {order.RaisonSocialClient && (
                                  <p className="mb-0 small text-muted">
                                    <i className="fas fa-building me-1"></i>
                                    {order.RaisonSocialClient}
                                  </p>
                                )}
                              </div>

                              <div className="col-md-2 text-center">
                                <div className="h5 mb-0 text-success">
                                  {parseFloat(order.TTC || order.tarif_ttc || 0).toFixed(2)} €
                                </div>
                                <small className="text-muted">TTC</small>
                              </div>

                              <div className="col-md-2 text-center">
                                <Badge bg={status.variant} className="px-3 py-2">
                                  {status.label}
                                </Badge>
                              </div>

                              <div className="col-md-2 text-end">
                                <div className="d-flex flex-column gap-2">
                                  {order.StatuPaiement === 3 && (
                                    <Button
                                      variant="outline-success"
                                      size="sm"
                                      onClick={() => handleDownloadInvoice(order)}
                                    >
                                      <i className="fas fa-download me-2"></i>
                                      Facture
                                    </Button>
                                  )}
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => history.push(`/historique/${order.id}`)}
                                  >
                                    <i className="fas fa-eye me-2"></i>
                                    Détails
                                  </Button>
                                </div>
                              </div>
                            </div>

                            {order.poids_label && (
                              <div className="mt-2 pt-2 border-top">
                                <small className="text-muted">
                                  <i className="fas fa-weight me-2"></i>
                                  Poids: {order.poids_label}
                                </small>
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />

      <style jsx>{`
        .hover-shadow {
          transition: all 0.3s ease;
        }
        .hover-shadow:hover {
          transform: translateY(-2px);
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
        }
      `}</style>
    </>
  );
};

export default OrderHistoryPage;
